import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisconnectMicrosoftEmailMutation } from 'src/common/hooks/useDisconnectMicrosoftEmailMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import ConfirmDeleteModal from './ConfirmDeleteModal';

type Props = {
  // Renders as a plain link in cards, or a bordered button on settings-style surfaces.
  variant?: 'subtle' | 'default';
  size?: string;
  onDisconnected?: () => void;
};

export function OutlookDisconnectButton({
  variant = 'subtle',
  size = 'compact-xs',
  onDisconnected,
}: Props) {
  const [confirmOpened, setConfirmOpened] = useState(false);
  const { disconnect, isLoading } = useDisconnectMicrosoftEmailMutation();

  const handleConfirm = async () => {
    // Hoisted out of the try block: the React Compiler cannot yet optimise value blocks
    // (optional chaining) inside try/catch.
    const notifyDisconnected = onDisconnected;
    try {
      await disconnect();
      setConfirmOpened(false);
      notifications.show({
        title: 'Outlook disconnected',
        message: 'You can connect a different Outlook account whenever you like.',
        color: 'green',
      });
      if (notifyDisconnected) notifyDisconnected();
    } catch (error) {
      notifyAndLogError(
        'Could not disconnect Outlook',
        'Something went wrong. Please try again.',
        error,
      );
    }
  };

  return (
    <>
      <Button variant={variant} color="gray" size={size} onClick={() => setConfirmOpened(true)}>
        Disconnect Outlook
      </Button>
      <ConfirmDeleteModal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        onConfirm={handleConfirm}
        title="Disconnect Outlook?"
        message="Siesta will no longer be able to read your mailbox or send email on your behalf. You can reconnect at any time — including with a different account."
        confirmLabel="Disconnect"
        isLoading={isLoading}
      />
    </>
  );
}
